/**
 * Configuration context and provider for app-wide configuration management
 */

import React, {
  createContext,
  use,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  type AppConfig,
  AppMode,
  DEFAULT_CONFIG,
  type ConfigContextType,
} from "./types";
import { STORAGE_KEY } from "./stored-config";

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(DEFAULT_CONFIG);

  // Load configuration from localStorage on mount
  const loadConfig = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<AppConfig>;
        // Merge with defaults to handle missing fields
        setConfig({ ...DEFAULT_CONFIG, ...parsed });
      }
    } catch (error) {
      console.error("Failed to load config from localStorage:", error);
      setConfig(DEFAULT_CONFIG);
    }
  }, []);

  // Save configuration to localStorage
  const saveConfig = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.error("Failed to save config to localStorage:", error);
    }
  }, [config]);

  // Update configuration
  const updateConfig = (updates: Partial<AppConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  // Computed properties
  const isLocalMode = config.mode === AppMode.LOCAL;
  const needsAuth = config.mode === AppMode.REMOTE;

  const getTenantsUrl = useCallback(() => {
    if (config.mode === AppMode.LOCAL) {
      return `${config.localBaseUrl}/api/v1`;
    }
    return config.tenantsBaseUrl;
  }, [config.mode, config.localBaseUrl, config.tenantsBaseUrl]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Save config whenever it changes
  useEffect(() => {
    saveConfig();
  }, [config, saveConfig]);

  const contextValue: ConfigContextType = useMemo(
    () => ({
      config,
      updateConfig,
      isLocalMode,
      needsAuth,
      getTenantsUrl,
      saveConfig,
      loadConfig,
    }),
    [config, isLocalMode, needsAuth, getTenantsUrl, saveConfig, loadConfig],
  );

  return <ConfigContext value={contextValue}>{children}</ConfigContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfig() {
  const context = use(ConfigContext);
  if (context === undefined) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
}
